import React, { useState } from 'react';
import { 
  BarChart3, FileText, Users, TrendingUp, Settings, Home, 
  FolderOpen, PieChart, Bell, ChevronLeft, ChevronRight,
  Plus, Search, Filter, MoreHorizontal, ArrowUpRight,
  CheckCircle, Clock, AlertTriangle, DollarSign
} from 'lucide-react';

// Demo page showing 3 different design styles
export default function StyleDemo() {
  const [activeStyle, setActiveStyle] = useState<'saas' | 'enterprise' | 'friendly'>('saas');

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Style Selector Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">🎨 Design Style Comparison</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveStyle('saas')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeStyle === 'saas' 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              A) Modern SaaS
            </button>
            <button
              onClick={() => setActiveStyle('enterprise')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeStyle === 'enterprise' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              B) Professional Enterprise
            </button>
            <button
              onClick={() => setActiveStyle('friendly')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeStyle === 'friendly' 
                  ? 'bg-gradient-to-r from-blue-500 to-pink-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              C) Friendly & Approachable
            </button>
          </div>
        </div>
      </div>

      {/* Style Preview */}
      <div className="max-w-7xl mx-auto p-6">
        {activeStyle === 'saas' && <ModernSaaSStyle />}
        {activeStyle === 'enterprise' && <ProfessionalEnterpriseStyle />}
        {activeStyle === 'friendly' && <FriendlyApproachableStyle />}
      </div>
    </div>
  );
}

// ============================================
// STYLE A: Modern SaaS (Linear, Notion style)
// ============================================
function ModernSaaSStyle() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex bg-white rounded-xl overflow-hidden border border-gray-200 min-h-[600px]">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-56'} bg-gray-50 border-r border-gray-200 transition-all duration-200 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">CS</span>
            </div>
            {!sidebarCollapsed && <span className="font-semibold text-gray-900">ClaimSync</span>}
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-2 space-y-1">
          {[
            { icon: Home, label: 'Dashboard', active: true },
            { icon: Plus, label: 'New Claim' },
            { icon: FileText, label: 'Reports' },
            { icon: FolderOpen, label: 'Cleanup' },
            { icon: PieChart, label: 'Analytics' },
          ].map((item, i) => (
            <button
              key={i}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                item.active 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md text-sm"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome back, here's your overview</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
              <Bell className="w-4 h-4" />
            </button>
            <button className="bg-gray-900 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-800">
              New Claim
            </button>
          </div>
        </div>

        {/* Stats Grid - Minimal Style */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Claims', value: '24', change: '+3 this week' },
            { label: 'In Progress', value: '8', change: '4 need review' },
            { label: 'Completed', value: '14', change: '+2 today' },
            { label: 'Total Value', value: '$2.4M', change: '+12% vs last month' },
          ].map((stat, i) => (
            <div key={i} className="p-4 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-400 mt-1">{stat.change}</p>
            </div>
          ))}
        </div>

        {/* Recent Claims - Clean Table */}
        <div className="border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-900">Recent Claims</h2>
            <button className="text-xs text-gray-500 hover:text-gray-700">View all →</button>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              { name: 'Taylor Church LLC', amount: '$125,000', status: 'In Progress', date: 'Dec 20' },
              { name: 'Metro Retail Inc', amount: '$89,500', status: 'Review', date: 'Dec 19' },
              { name: 'Sunrise Cafe', amount: '$45,200', status: 'Complete', date: 'Dec 18' },
            ].map((claim, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                    <FileText className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{claim.name}</p>
                    <p className="text-xs text-gray-500">{claim.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-900">{claim.amount}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    claim.status === 'Complete' ? 'bg-gray-100 text-gray-600' :
                    claim.status === 'Review' ? 'bg-yellow-50 text-yellow-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {claim.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// STYLE B: Professional Enterprise (Salesforce style)
// ============================================
function ProfessionalEnterpriseStyle() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex bg-gray-50 rounded-xl overflow-hidden border border-gray-300 min-h-[600px]">
      {/* Sidebar - Dense with sections */}
      <div className={`${sidebarCollapsed ? 'w-14' : 'w-60'} bg-[#0b2545] text-white transition-all duration-200 flex flex-col`}>
        {/* Logo */}
        <div className="p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            {!sidebarCollapsed && <span className="font-bold text-white">ClaimSync Pro</span>}
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-3">
            <div className="flex items-center gap-2 bg-white/10 rounded px-3 py-2">
              <Search className="w-4 h-4 text-white/60" />
              <input 
                type="text" 
                placeholder="Search..." 
                className="bg-transparent text-sm text-white placeholder-white/40 outline-none w-full"
              />
            </div>
          </div>
        )}

        {/* Nav Sections */}
        <nav className="flex-1 overflow-y-auto">
          {/* Main Section */}
          {!sidebarCollapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-blue-300 uppercase tracking-wider">Main</p>
          )}
          <div className="px-2 space-y-0.5">
            {[
              { icon: Home, label: 'Dashboard', active: true, badge: null },
              { icon: Plus, label: 'New Claim', badge: null },
              { icon: FileText, label: 'All Claims', badge: '24' },
              { icon: Clock, label: 'Pending Review', badge: '8' },
            ].map((item, i) => (
              <button
                key={i}
                className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition ${
                  item.active 
                    ? 'bg-blue-600 text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </div>
                {!sidebarCollapsed && item.badge && (
                  <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded">{item.badge}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tools Section */}
          {!sidebarCollapsed && (
            <p className="px-3 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider">Tools</p>
          )}
          <div className="px-2 space-y-0.5">
            {[
              { icon: FolderOpen, label: 'Data Cleanup' },
              { icon: PieChart, label: 'Analytics' },
              { icon: Users, label: 'Team' },
              { icon: Settings, label: 'Settings' },
            ].map((item, i) => (
              <button
                key={i}
                className="w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-white/70 hover:bg-white/10 hover:text-white"
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
          </div>
        </nav>

        {/* Collapse */}
        <div className="p-2 border-t border-white/10">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center px-3 py-2 text-white/50 hover:bg-white/10 rounded text-sm"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-gray-900">Claims Dashboard</h1>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">
                <Filter className="w-3 h-3" />
                Filters
              </button>
              <select className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 bg-white">
                <option>Last 30 days</option>
                <option>Last 90 days</option>
                <option>This year</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 text-gray-500 hover:text-gray-700">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Claim
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {/* KPI Cards - Data Dense */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { icon: FileText, label: 'Total Claims', value: '24', trend: '+12%', color: 'blue' },
              { icon: Clock, label: 'Pending', value: '8', trend: '-2', color: 'yellow' },
              { icon: CheckCircle, label: 'Completed', value: '14', trend: '+5', color: 'green' },
              { icon: DollarSign, label: 'Total Value', value: '$2.4M', trend: '+18%', color: 'purple' },
            ].map((kpi, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${
                    kpi.color === 'blue' ? 'bg-blue-100' :
                    kpi.color === 'yellow' ? 'bg-yellow-100' :
                    kpi.color === 'green' ? 'bg-green-100' :
                    'bg-blue-100'
                  }`}>
                    <kpi.icon className={`w-5 h-5 ${
                      kpi.color === 'blue' ? 'text-blue-600' :
                      kpi.color === 'yellow' ? 'text-yellow-600' :
                      kpi.color === 'green' ? 'text-green-600' :
                      'text-blue-600'
                    }`} />
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    kpi.trend.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {kpi.trend}
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-3">{kpi.value}</p>
                <p className="text-sm text-gray-500">{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* Data Table - Enterprise Style */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h2 className="font-semibold text-gray-900">Recent Claims</h2>
              <div className="flex items-center gap-2">
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Export</button>
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Claim</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Amount</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { name: 'Taylor Church LLC', type: 'Hurricane', amount: '$125,000', status: 'In Progress', date: 'Dec 20, 2024' },
                  { name: 'Metro Retail Inc', type: 'Fire', amount: '$89,500', status: 'Review', date: 'Dec 19, 2024' },
                  { name: 'Sunrise Cafe', type: 'Flood', amount: '$45,200', status: 'Complete', date: 'Dec 18, 2024' },
                ].map((claim, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-900">{claim.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{claim.type}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{claim.amount}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        claim.status === 'Complete' ? 'bg-green-100 text-green-700' :
                        claim.status === 'Review' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {claim.status === 'Complete' && <CheckCircle className="w-3 h-3" />}
                        {claim.status === 'Review' && <AlertTriangle className="w-3 h-3" />}
                        {claim.status === 'In Progress' && <Clock className="w-3 h-3" />}
                        {claim.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{claim.date}</td>
                    <td className="px-4 py-3">
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// STYLE C: Friendly & Approachable (Stripe, Loom style)
// ============================================
function FriendlyApproachableStyle() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex bg-gradient-to-br from-violet-50 via-white to-cyan-50 rounded-2xl overflow-hidden border border-gray-200 min-h-[600px]">
      {/* Sidebar - Rounded, Colorful */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-60'} bg-white/80 backdrop-blur border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-200">
              <span className="text-white text-lg font-bold">C</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="font-bold text-gray-900">ClaimSync</span>
                <p className="text-xs text-gray-500">Claims made easy</p>
              </div>
            )}
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-3 space-y-1">
          {[
            { icon: Home, label: 'Home', active: true, color: 'violet' },
            { icon: Plus, label: 'New Claim', color: 'green' },
            { icon: FileText, label: 'My Claims', color: 'blue' },
            { icon: FolderOpen, label: 'Cleanup', color: 'orange' },
            { icon: PieChart, label: 'Insights', color: 'pink' },
          ].map((item, i) => (
            <button
              key={i}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                item.active 
                  ? 'bg-gradient-to-r from-violet-500 to-blue-600 text-white shadow-lg shadow-violet-200' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className={`${item.active ? '' : `p-1.5 rounded-lg ${
                item.color === 'green' ? 'bg-green-100' :
                item.color === 'blue' ? 'bg-blue-100' :
                item.color === 'orange' ? 'bg-orange-100' :
                item.color === 'pink' ? 'bg-pink-100' :
                'bg-violet-100'
              }`}`}>
                <item.icon className={`w-4 h-4 ${item.active ? 'text-white' : 
                  item.color === 'green' ? 'text-green-600' :
                  item.color === 'blue' ? 'text-blue-600' :
                  item.color === 'orange' ? 'text-orange-600' :
                  item.color === 'pink' ? 'text-pink-600' :
                  'text-violet-600'
                }`} />
              </div>
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* User Section */}
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-gray-200">
            <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-100 cursor-pointer">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">JD</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">John Doe</p>
                <p className="text-xs text-gray-500 truncate">john@example.com</p>
              </div>
            </div>
          </div>
        )}

        {/* Collapse */}
        <div className="p-3">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 p-2 text-gray-400 hover:bg-gray-100 rounded-xl"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Header - Playful */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hey there! 👋</h1>
            <p className="text-gray-500">Here's what's happening with your claims</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2.5 bg-white rounded-xl shadow-sm hover:shadow-md transition border border-gray-200">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">3</span>
            </button>
            <button className="bg-gradient-to-r from-violet-500 to-blue-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-violet-200 hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Claim
            </button>
          </div>
        </div>

        {/* Stats - Card Style with Illustrations */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { icon: FileText, label: 'Total Claims', value: '24', bg: 'from-violet-500 to-blue-600', shadow: 'shadow-violet-200' },
            { icon: Clock, label: 'In Progress', value: '8', bg: 'from-amber-400 to-orange-500', shadow: 'shadow-orange-200' },
            { icon: CheckCircle, label: 'Completed', value: '14', bg: 'from-emerald-400 to-green-500', shadow: 'shadow-green-200' },
            { icon: TrendingUp, label: 'Recovery Rate', value: '87%', bg: 'from-cyan-400 to-blue-500', shadow: 'shadow-blue-200' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer group border border-gray-100">
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.bg} rounded-xl flex items-center justify-center mb-4 shadow-lg ${stat.shadow} group-hover:scale-110 transition-transform`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Claims - Card List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-gray-900">Recent Claims</h2>
            <button className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
              View all <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              { name: 'Taylor Church LLC', amount: '$125,000', status: 'In Progress', color: 'blue', emoji: '🏢' },
              { name: 'Metro Retail Inc', amount: '$89,500', status: 'Review', color: 'amber', emoji: '🏪' },
              { name: 'Sunrise Cafe', amount: '$45,200', status: 'Complete', color: 'green', emoji: '☕' },
            ].map((claim, i) => (
              <div key={i} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                    {claim.emoji}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{claim.name}</p>
                    <p className="text-sm text-gray-500">{claim.amount}</p>
                  </div>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  claim.color === 'green' ? 'bg-green-100 text-green-700' :
                  claim.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {claim.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
